// =============================================================================
// FontsPanel — platform font catalog management
// =============================================================================
// SYSTEM fonts uploaded here become available to every store's designer text
// tool (and to the export glyph rasterizer — the SAME binary renders the
// print file, so what admins upload is exactly what prints).
//
// All font metadata (family, weight, style, variable-axis detection) is
// extracted server-side from the binary — the UI never asks for it. Upload
// rejections (not a font, font collection, embedding-restricted license,
// color font) surface verbatim from the API.
// =============================================================================

import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardHeader,
  Input,
  Spinner,
  useConfirm,
} from '@tkpod/ui';

import { api } from '@/lib/api';
import { getCsrfToken } from '@/lib/csrf';
import { toast } from '@/lib/toast';

interface FontFileView {
  id: string;
  weight: number;
  style: 'NORMAL' | 'ITALIC';
  format: 'TTF' | 'OTF' | 'WOFF' | 'WOFF2';
  fileSize: number;
  postscriptName: string | null;
  glyphCount: number | null;
}

interface FontView {
  id: string;
  scope: 'SYSTEM' | 'STORE';
  organizationId: string | null;
  connectionId: string | null;
  family: string;
  displayName: string;
  isActive: boolean;
  isVariable: boolean;
  files: FontFileView[];
}

interface UploadResult {
  font: FontView;
  file: FontFileView;
  duplicateWarning?: string;
}

const FONTS_URL = '/api/v1/admin/fonts';

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function styleLabel(file: FontFileView): string {
  const weight =
    file.weight === 400 ? 'Regular' : file.weight === 700 ? 'Bold' : String(file.weight);
  return file.style === 'ITALIC' ? `${weight} Italic` : weight;
}

async function uploadFontFile(file: File): Promise<UploadResult> {
  const baseUrl = import.meta.env.VITE_API_URL as string;
  const formData = new FormData();
  formData.append('file', file);
  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const res = await fetch(`${baseUrl}${FONTS_URL}/upload`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: formData,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message) {
        message = Array.isArray(body.message)
          ? body.message.join(', ')
          : body.message;
      }
    } catch {
      // Keep the status-based message.
    }
    throw new Error(message);
  }
  return (await res.json()) as UploadResult;
}

export function FontsPanel() {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'ALL' | 'SYSTEM' | 'STORE'>('ALL');
  const [uploading, setUploading] = useState(false);

  const { data: fonts, isLoading, error } = useQuery<FontView[]>({
    queryKey: ['admin', 'fonts'],
    queryFn: () => api.get<FontView[]>(FONTS_URL),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'fonts'] });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { displayName?: string; isActive?: boolean } }) =>
      api.patch<FontView>(`${FONTS_URL}/${id}`, body),
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err: Error) => toast(err.message || 'Update failed.', 'error'),
  });

  const deleteFontMut = useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`${FONTS_URL}/${id}`),
    onSuccess: async () => {
      toast('Font deleted.', 'success');
      await invalidate();
    },
    onError: (err: Error) => toast(err.message || 'Delete failed.', 'error'),
  });

  const deleteFileMut = useMutation({
    mutationFn: ({ fontId, fileId }: { fontId: string; fileId: string }) =>
      api.delete<{ deleted: boolean }>(`${FONTS_URL}/${fontId}/files/${fileId}`),
    onSuccess: async () => {
      toast('Font file removed.', 'success');
      await invalidate();
    },
    onError: (err: Error) => toast(err.message || 'Delete failed.', 'error'),
  });

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Sequential so per-family attach (Regular + Bold + Italic of the same
      // family) never races the Font row creation.
      for (const file of Array.from(files)) {
        const result = await uploadFontFile(file);
        if (result.duplicateWarning) {
          toast(result.duplicateWarning, 'info');
        } else {
          toast(
            `Uploaded "${result.font.family}" ${styleLabel(result.file)}.`,
            'success',
          );
        }
      }
      await invalidate();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Upload failed.', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const visibleFonts = useMemo(() => {
    let list = fonts ?? [];
    if (scopeFilter !== 'ALL') list = list.filter((f) => f.scope === scopeFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (f) =>
          f.family.toLowerCase().includes(q) ||
          f.displayName.toLowerCase().includes(q),
      );
    }
    return list;
  }, [fonts, scopeFilter, search]);

  if (error && !fonts) {
    return (
      <Card>
        <CardHeader title="Fonts" description={DESCRIPTION} />
        <div className="mt-4 rounded-md bg-error-muted p-4 text-sm text-error">
          Failed to load: {error instanceof Error ? error.message : String(error)}
        </div>
      </Card>
    );
  }

  if (isLoading || !fonts) {
    return (
      <Card>
        <CardHeader title="Fonts" description={DESCRIPTION} />
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {dialog}
      <CardHeader title="Fonts" description={DESCRIPTION} />

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            label="Search"
            placeholder="Family or display name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-text-secondary">
            Scope
          </span>
          <select
            className="h-9 rounded-md border border-border-subtle bg-surface px-2 text-sm text-text-primary"
            value={scopeFilter}
            onChange={(e) =>
              setScopeFilter(e.target.value as 'ALL' | 'SYSTEM' | 'STORE')
            }
          >
            <option value="ALL">All</option>
            <option value="SYSTEM">System</option>
            <option value="STORE">Store</option>
          </select>
        </label>
        <div className="ml-auto">
          <input
            ref={fileInputRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <Button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload system font'}
          </Button>
        </div>
      </div>

      <p className="mt-2 text-xs text-text-tertiary">
        TTF / OTF / WOFF / WOFF2. Family, weight and style are read from the
        binary. Upload Regular, Bold and Italic files of the same family to
        complete a style set. Font collections (.ttc) and
        embedding-restricted fonts are rejected.
      </p>

      <div className="mt-6 space-y-4">
        {visibleFonts.length === 0 && (
          <p className="py-8 text-center text-sm text-text-tertiary">
            No fonts match. Builtin families (Arial, Helvetica, Times New
            Roman, Courier New) are always available and are not listed here.
          </p>
        )}
        {visibleFonts.map((font) => (
          <FontRow
            key={font.id}
            font={font}
            pending={updateMut.isPending || deleteFontMut.isPending || deleteFileMut.isPending}
            onRename={(displayName) =>
              updateMut.mutate({ id: font.id, body: { displayName } })
            }
            onToggleActive={(isActive) =>
              updateMut.mutate({ id: font.id, body: { isActive } })
            }
            onDeleteFont={async () => {
              const ok = await confirm({
                title: 'Delete font',
                description: `"${font.displayName}" and all its files will be permanently removed. Saved designs using it will fall back at export.`,
                confirmLabel: 'Delete',
                tone: 'danger',
              });
              if (ok) deleteFontMut.mutate(font.id);
            }}
            onDeleteFile={async (file) => {
              const ok = await confirm({
                title: 'Remove font file',
                description: `Remove the ${styleLabel(file)} file from "${font.displayName}"?`,
                confirmLabel: 'Remove',
                tone: 'danger',
              });
              if (ok) deleteFileMut.mutate({ fontId: font.id, fileId: file.id });
            }}
          />
        ))}
      </div>
    </Card>
  );
}

const DESCRIPTION =
  'Managed fonts for the designer text tool. System fonts are available to every store; store fonts are uploaded by merchants and scoped to their organization.';

function FontRow({
  font,
  pending,
  onRename,
  onToggleActive,
  onDeleteFont,
  onDeleteFile,
}: {
  font: FontView;
  pending: boolean;
  onRename: (displayName: string) => void;
  onToggleActive: (isActive: boolean) => void;
  onDeleteFont: () => void;
  onDeleteFile: (file: FontFileView) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(font.displayName);

  return (
    <div className="rounded-lg border border-border-subtle p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-label="Display name"
              />
              <Button
                type="button"
                size="sm"
                disabled={pending || name.trim().length === 0}
                onClick={() => {
                  onRename(name.trim());
                  setEditing(false);
                }}
              >
                Save
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setName(font.displayName);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-text-primary">
                {font.displayName}
              </span>
              {font.displayName !== font.family && (
                <span className="truncate text-xs text-text-tertiary">
                  ({font.family})
                </span>
              )}
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setEditing(true)}
              >
                Rename
              </button>
            </div>
          )}
        </div>

        <span
          className={[
            'rounded-full px-2 py-0.5 text-xs font-medium',
            font.scope === 'SYSTEM'
              ? 'bg-info-50 text-info-700'
              : 'bg-surface-secondary text-text-secondary',
          ].join(' ')}
        >
          {font.scope === 'SYSTEM' ? 'System' : 'Store'}
        </span>
        {font.isVariable && (
          <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-xs text-text-secondary">
            Variable
          </span>
        )}

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={font.isActive}
            disabled={pending}
            onChange={(e) => onToggleActive(e.target.checked)}
            className="h-4 w-4 rounded border-border-subtle"
          />
          Active
        </label>

        <Button
          type="button"
          size="sm"
          variant="danger"
          disabled={pending}
          onClick={onDeleteFont}
        >
          Delete
        </Button>
      </div>

      <ul className="mt-3 flex flex-wrap gap-2">
        {font.files.map((file) => (
          <li
            key={file.id}
            className="flex items-center gap-2 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary"
          >
            <span className="font-medium text-text-primary">
              {styleLabel(file)}
            </span>
            <span>{file.format}</span>
            <span>{formatSize(file.fileSize)}</span>
            {typeof file.glyphCount === 'number' && (
              <span>{file.glyphCount} glyphs</span>
            )}
            <button
              type="button"
              className="text-error hover:underline"
              disabled={pending}
              onClick={() => onDeleteFile(file)}
              aria-label={`Remove ${styleLabel(file)} file`}
            >
              ✕
            </button>
          </li>
        ))}
        {font.files.length === 0 && (
          <li className="text-xs text-text-tertiary">No files.</li>
        )}
      </ul>
    </div>
  );
}
